import { ref } from 'vue';

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  duration?: number
}

// Global toast state
const toasts = ref<Toast[]>([]);

export function useToast() {
  /**
   * Show a toast notification
   */
  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info',
    duration = 3000
  ) => {
    const id = Math.random().toString(36).substring(2, 9);
    const toast: Toast = {
      id,
      message,
      type,
      duration,
    };

    toasts.value.push(toast);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  /**
   * Remove a toast by id
   */
  const removeToast = (id: string) => {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  };

  /**
   * Clear all toasts
   */
  const clearAll = () => {
    toasts.value = [];
  };

  /**
   * Show success toast
   */
  const success = (message: string, duration?: number) => {
    return showToast(message, 'success', duration);
  };

  /**
   * Show error toast
   */
  const error = (message: string, duration?: number) => {
    return showToast(message, 'error', duration);
  };

  /**
   * Show info toast
   */
  const info = (message: string, duration?: number) => {
    return showToast(message, 'info', duration);
  };

  /**
   * Show warning toast
   */
  const warning = (message: string, duration?: number) => {
    return showToast(message, 'warning', duration);
  };

  return {
    toasts,
    showToast,
    removeToast,
    clearAll,
    success,
    error,
    info,
    warning,
  };
}
